// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import Alert from "src/components/shared/alerts/Alert/Alert";
import {
  FastForwardIcon,
  PauseIcon,
  PlayIcon,
  RestartIcon,
  TrashIcon,
  WarningIcon,
} from "src/assets/icons";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Constants
import { DeviceStagesTimeline, examCardEnums } from "src/constants/enums";

// Services
import {
  skipExamRequest,
  terminateExamRequest,
  updateDeviceStage,
} from "src/services/signalR/api";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Styles
import {
  ModalCustomCancel,
  ModalCustomWarning,
  ModalCustomDeleteSolid,
  ModalCustomDelete,
} from "src/styles/mui/muiStylesHelper";

// Utils
import {
  getReportExamsSortedByGroupId,
  isMultiExamPackage,
} from "src/utils/exams/examReportsHelper";

type LiveUpdateButtonsType = {
  connection: HubConnection | null;
  isLoadingSignalR: boolean;
};

const LiveUpdateButtons = ({
  connection,
  isLoadingSignalR,
}: LiveUpdateButtonsType) => {
  const { t } = useTranslation();
  const { getToken } = useTokenAccess();

  const [openSkipModal, setOpenSkipModal] = useState(false);
  const [openTerminateModal, setOpenTerminateModal] = useState(false);

  const {
    exam,
    currentExamSection,
    device,
    isPaused,
    isConfirmation,
    inTutorial,
    bothControllersDisconnected,
    hmdInProximity,
    activeStep,
    actions,
  } = useLiveUpdateContext();

  const { setExamReportLive } = actions;

  const hasMultipleMonocularSections = useMemo(() => {
    if (exam) {
      const allExamSections = getReportExamsSortedByGroupId(exam);
      const hasMultipleSections =
        allExamSections.filter(
          (section) =>
            section.groupId === currentExamSection?.groupId &&
            !section.completionDate
        ).length > 1;
      return hasMultipleSections;
    }

    return false;
  }, [exam, currentExamSection]);

  const isMultiPackage = useMemo(
    () => Boolean(exam && isMultiExamPackage(exam)),
    [exam]
  );

  const disableRestartTermination = useMemo(
    () =>
      (activeStep.value !== DeviceStagesTimeline.Exam &&
        activeStep.value !== DeviceStagesTimeline.Tutorial) ||
      isLoadingSignalR,
    [activeStep, isLoadingSignalR]
  );

  // handle the update of a device's stage
  const handleUpdateDeviceStage = useCallback(
    async (newStage: string) => {
      if (!device?.id || !connection) return;

      try {
        const token = await getToken();
        const response = await updateDeviceStage(
          token,
          device?.id,
          newStage,
          connection
        );
        const data = JSON.parse(response);
        if (data?.state === true && newStage === "Confirmation") {
          setExamReportLive(undefined);
        }
      } catch (error) {
        console.error("Could not update device stage", error);
      }
    },
    [device?.id, connection, setExamReportLive, getToken]
  );

  const handleDeviceExamSkipRequest = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await skipExamRequest(token, device?.id, connection);
    } catch (error) {
      console.error("Could not skip", error);
    }
  }, [connection, device?.id, getToken]);

  const handleDeviceExamTerminationRequest = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await terminateExamRequest(token, device?.id, connection);
    } catch (error) {
      console.error("Could not terminate", error);
    }
  }, [connection, device?.id, getToken]);

  const leftExamCanStart = useMemo(() => {
    if (!exam || !currentExamSection) return false;

    // get the current exam section being tested
    const currentExamType = currentExamSection?.type;
    const enumValues = Object.values(examCardEnums);
    const currentSectionName = enumValues.find(
      (value) => value.type === currentExamType
    )?.sectionName;

    if (!currentSectionName) return false;

    const sections = exam?.[currentSectionName];

    return (
      sections &&
      sections?.length > 1 &&
      sections[0].completionDate === null &&
      device?.deviceStatus?.stage?.stageType === "Congratulations"
    );
  }, [exam, currentExamSection, device]);

  const isPlayMode = useMemo(
    () => isPaused || isConfirmation || leftExamCanStart,
    [isPaused, isConfirmation, leftExamCanStart]
  );

  const disablePlayPauseButton = useMemo(
    () =>
      (activeStep.value !== DeviceStagesTimeline.Exam &&
        activeStep.value !== DeviceStagesTimeline.Confirmation &&
        !inTutorial &&
        !leftExamCanStart) ||
      isLoadingSignalR ||
      ((bothControllersDisconnected || !hmdInProximity) &&
        (isPaused || isConfirmation || leftExamCanStart)),
    [
      activeStep,
      inTutorial,
      leftExamCanStart,
      bothControllersDisconnected,
      hmdInProximity,
      isConfirmation,
      isPaused,
      isLoadingSignalR,
    ]
  );

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      width="100%"
      gap="8px"
      sx={{ opacity: isLoadingSignalR ? 0.4 : 1 }}
    >
      {/* Pause / Play button */}
      <Tooltip
        title={isPlayMode ? t("Start or resume the exam") : t("Pause the exam")}
        disableHoverListener={disablePlayPauseButton}
      >
        <span style={{ flex: 1 }}>
          <Button
            fullWidth
            startIcon={
              isPlayMode ? (
                <PlayIcon height="20px" width="20px" />
              ) : (
                <PauseIcon height="20px" width="20px" />
              )
            }
            aria-label="pause and resume button"
            sx={ModalCustomCancel}
            disabled={disablePlayPauseButton}
            onClick={() =>
              handleUpdateDeviceStage(
                isPaused && inTutorial
                  ? "Tutorial"
                  : isPlayMode
                    ? "Exam"
                    : "Pause"
              )
            }
          >
            <Typography fontSize="14px">
              {isPlayMode ? t("Play") : t("Pause")}
            </Typography>
          </Button>
        </span>
      </Tooltip>

      {/* Restart button */}
      <Tooltip
        title={t("Restart the exam")}
        disableHoverListener={disableRestartTermination}
      >
        <span style={{ flex: 1 }}>
          <Button
            fullWidth
            startIcon={<RestartIcon height="20px" width="20px" />}
            aria-label="restart button"
            sx={ModalCustomCancel}
            disabled={disableRestartTermination}
            onClick={() => handleUpdateDeviceStage("Confirmation")} // Restart the exam
          >
            <Typography fontSize="14px">{t("Restart")}</Typography>
          </Button>
        </span>
      </Tooltip>

      {/* Skip tutorial button */}
      {inTutorial && !isLoadingSignalR && (
        <Tooltip
          title={t("Skip the tutorial")}
          disableHoverListener={disableRestartTermination}
        >
          <span style={{ flex: 1 }}>
            <Button
              fullWidth
              startIcon={<FastForwardIcon height="20px" width="20px" />}
              aria-label="skip tutorial button"
              sx={ModalCustomWarning}
              onClick={() => handleUpdateDeviceStage("Exam")} // Skip the tutorial
            >
              <Typography fontSize="14px">{t("Skip")}</Typography>
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Skip exam button */}
      {(isMultiPackage || hasMultipleMonocularSections) && (
        <Tooltip
          title={t("Skip the exam")}
          disableHoverListener={disableRestartTermination}
        >
          <span style={{ flex: 1 }}>
            <Button
              fullWidth
              startIcon={<WarningIcon height="20px" width="20px" />}
              aria-label="skip exam button"
              sx={ModalCustomDelete}
              disabled={disableRestartTermination}
              onClick={() => setOpenSkipModal(true)}
            >
              <Typography fontSize="14px" whiteSpace="nowrap">
                {t("Skip Exam")}
              </Typography>
            </Button>
          </span>
        </Tooltip>
      )}

      <SharedModal
        open={openSkipModal}
        title={t("Skip Exam Section?")}
        modalDescription={
          <Alert
            variant="error"
            alertContent={t(
              "This action is irreversible — you will not be able to return to this exam section later."
            )}
          />
        }
        confirmButtonVariant="error"
        confirmButtonText={t("Skip Exam")}
        onClose={() => setOpenSkipModal(false)}
        onConfirm={() => {
          handleDeviceExamSkipRequest().then(() => setOpenSkipModal(false));
        }}
      />

      {/* Terminate button */}
      <Tooltip
        title={t("Terminate the exam")}
        disableHoverListener={disableRestartTermination}
      >
        <span style={{ flex: 1 }}>
          <Button
            fullWidth
            startIcon={<TrashIcon height="20px" width="20px" />}
            aria-label="terminate button"
            sx={ModalCustomDeleteSolid}
            disabled={disableRestartTermination}
            onClick={() => setOpenTerminateModal(true)}
          >
            <Typography fontSize="14px">{t("Terminate")}</Typography>
          </Button>
        </span>
      </Tooltip>

      <SharedModal
        open={openTerminateModal}
        title={t("Terminate Exam?")}
        modalDescription={
          <Alert
            variant="error"
            alertContent={t(
              "This will permanently end all exams in the current session. Any incomplete exams cannot be restarted."
            )}
          />
        }
        confirmButtonVariant="error"
        confirmButtonText={t("Terminate Exam")}
        onClose={() => setOpenTerminateModal(false)}
        onConfirm={() => {
          handleDeviceExamTerminationRequest().then(() =>
            setOpenTerminateModal(false)
          );
        }}
      />
    </Box>
  );
};

export default LiveUpdateButtons;
